import React from 'react';
import { Box, ButtonBase, Drawer, Toolbar, Typography, useTheme } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NavSection from './NavSection';
import { useSectionExpandedState } from './useSectionExpandedState';
const drawerWidth = 280;

/** Opaque chrome surface — ambient glass retired in A3 (DR-1 / D4). */
const chromeDrawerPaper = (theme) => {
  const chrome = theme.palette.ef?.chrome ?? theme.palette.background.paper;
  const edge = theme.palette.ef?.edge ?? theme.palette.divider;
  return {
    boxSizing: 'border-box',
    width: drawerWidth,
    backgroundImage: 'none',
    boxShadow: 'none',
    backgroundColor: chrome,
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
    borderRight: `1px solid ${edge}`,
  };
};

/**
 * @param {object} props
 * @param {boolean} props.mobileOpen
 * @param {() => void} props.onDrawerToggle
 * @param {string} props.brandPath
 * @param {Array<{ id: string, title: string, initiallyExpanded?: boolean, items: Array<{ id: string, label: string, path?: string, matchPath?: string, icon?: React.ReactNode, action?: string }> }>} props.sections
 */
const Sidebar = ({ mobileOpen, onDrawerToggle, brandPath, sections }) => {
  const theme = useTheme();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = theme.palette.mode === 'dark';

  const { isExpanded, toggle } = useSectionExpandedState(sections, location.pathname);

  const handleNavigate = (path) => {
    navigate(path);
    if (mobileOpen) onDrawerToggle();
  };

  const handleBrandClick = () => {
    const raw = brandPath || '/faculty/dashboard';
    const target = String(raw).startsWith('/') ? raw : `/${raw}`;
    navigate(target);
    if (mobileOpen) onDrawerToggle();
  };

  const handleItemActivate = (item) => {
    if (item.action === 'logout') {
      logout();
      if (mobileOpen) onDrawerToggle();
      return;
    }
    if (item.path) handleNavigate(item.path);
  };

  const brandBorder = isDark
    ? '1px solid rgba(255, 255, 255, 0.06)'
    : '1px solid rgba(15, 23, 42, 0.06)';

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar
        sx={{
          minHeight: { xs: 64, sm: 72 },
          px: 2.5,
          py: 1.5,
          borderBottom: brandBorder,
        }}
      >
        <ButtonBase
          type="button"
          onClick={handleBrandClick}
          disableRipple
          aria-label="Go to home"
          sx={{
            textAlign: 'left',
            borderRadius: '10px',
            px: 1,
            py: 0.75,
            mx: -0.5,
            transition: 'background-color 0.2s ease',
            '&:hover': {
              bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.04)',
            },
          }}
        >
          <Typography
            variant="h2"
            component="span"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.03em',
              color: isDark ? 'rgba(255, 255, 255, 0.95)' : 'text.primary',
              fontSize: '1.625rem',
            }}
          >
            StackTrack
          </Typography>
        </ButtonBase>
      </Toolbar>

      <Box
        component="nav"
        sx={{
          flex: 1,
          overflowY: 'auto',
          py: 3,
          px: 1.75,
        }}
      >
        {sections.map((section) => (
          <Box
            key={section.id}
            sx={{
              mb: section.collapsible === false ? 2 : 3.25,
              ...(section.id === 'profile' && {
                pt: 2,
                mt: 0.5,
                borderTop: '1px solid',
                borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'divider',
              }),
            }}
          >
            <NavSection
              sectionId={section.id}
              title={section.title ?? ''}
              items={section.items}
              pathname={location.pathname}
              onItemActivate={handleItemActivate}
              expanded={isExpanded(section.id)}
              onToggle={() => toggle(section.id)}
              collapsible={section.collapsible !== false}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      aria-label="sidebar navigation"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={(t) => ({
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': chromeDrawerPaper(t),
        })}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={(t) => ({
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': chromeDrawerPaper(t),
        })}
        open
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};

export { drawerWidth };
export default Sidebar;
